import type { AriaAttributes, AriaRole, KeyboardEventHandler } from "react";

/**
 * What a trigger's focusable control carries: the widget role, its place in the
 * tab order, the id `aria-controls` and labels point at, the keyboard handler
 * of the list interactions, and every `aria-*` the role needs.
 */
export type IDropdownControlProps = AriaAttributes & {
  role?: AriaRole;
  id?: string;
  tabIndex?: number;
  onKeyDown?: KeyboardEventHandler<HTMLElement>;
};

const CONTROL_PROP_KEYS = new Set(["role", "tabIndex", "id", "onKeyDown"]);

const isControlProp = (key: string): boolean => {
  return CONTROL_PROP_KEYS.has(key) || key.startsWith("aria-");
};

export type ISplitReferenceProps = {
  anchorProps: Record<string, unknown>;
  controlProps: Record<string, unknown>;
};

/** Widget props go to the focusable control; pointer props stay on the anchor box. */
export const splitReferenceProps = (
  referenceProps: Record<string, unknown>,
): ISplitReferenceProps => {
  const anchorProps: Record<string, unknown> = {};
  const controlProps: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(referenceProps)) {
    if (isControlProp(key)) {
      controlProps[key] = value;
    } else {
      anchorProps[key] = value;
    }
  }

  return { anchorProps, controlProps };
};

export type IAriaName = {
  "aria-label": string | undefined;
  "aria-labelledby": string | undefined;
};

/**
 * One accessible name, never two: `ariaLabelledby` beats `ariaLabel`, and the
 * fallback (the trigger's id) applies only when the caller gave neither.
 */
export const resolveAriaName = (
  ariaLabel: string | undefined,
  ariaLabelledby: string | undefined,
  fallbackLabelledby?: string,
): IAriaName => {
  return {
    "aria-label": ariaLabelledby ? undefined : ariaLabel,
    "aria-labelledby":
      ariaLabelledby ?? (ariaLabel ? undefined : fallbackLabelledby),
  };
};
